import { Component, Input, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { LanguageService } from '@gs-2.0/core';
import { Subscription } from 'rxjs';

export interface HeroStat {
  value: string;
  aspect: string;
}

@Component({
  selector: 'app-hero-section',
  standalone: false,
  templateUrl: './hero-section.component.html',
  styleUrls: ['./hero-section.component.scss']
})
export class HeroSectionComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() variant: 'default' | 'simple' | 'storyAndMetrics' = 'default';
  @Input() backgroundImage: string = '';
  @Input() eyebrowIcon: string = '';
  @Input() eyebrowIconColor: string = '#FFC609';
  @Input() eyebrowText: string = '';
  @Input() headline: string = '';
  @Input() subtext: string = '';
  @Input() buttonLabel: string = '';
  @Input() stats: HeroStat[] = [];
  @Input() onButtonClick?: () => void;
  @Input() headerColor: string = '';
  @Input() subheaderColor: string = '';
  @Input() backgroundColor: string = '';
  @Input() statBannerBgColor: string = '';
  @Input() statItemColor: string = '';
  // Optional inputs for rendering query in italic (e.g., "Search Results for 'query'")
  @Input() headlineBeforeQuery?: string;
  @Input() queryText?: string;
  @Input() headlineAfterQuery?: string;
  @ViewChild('footerPadding', { static: false }) footerPaddingRef?: ElementRef<HTMLDivElement>;
  @ViewChild('statsBanner', { static: false }) statsBannerRef?: ElementRef<HTMLDivElement>;
  @ViewChild('heroSection', { static: false }) heroSectionRef?: ElementRef<HTMLDivElement>;

  headlineProcessed: string[] = [ '', '', '' ]
  isMobile: boolean = false;

  private translationSubscription?: Subscription;
  private resizeObserver?: ResizeObserver;
  private resizeListener = () => this.updateFooterPadding();

  constructor(private languageService: LanguageService) {
    this.checkScreenSize();
  }

  ngOnInit(): void {
    this.translationSubscription = this.languageService.translations$.subscribe(() => {
      this.processHeadline();
      // Update footer padding after translations change (stats labels might change size)
      setTimeout(() => this.updateFooterPadding(), 0);
    });
  }

  ngAfterViewInit(): void {
    // Delay initial calculation to ensure DOM is fully rendered
    setTimeout(() => this.updateFooterPadding(), 0);

    if (typeof window !== 'undefined' && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateFooterPadding();
      });

      if (this.statsBannerRef?.nativeElement) {
        this.resizeObserver.observe(this.statsBannerRef.nativeElement);
      }
      if (this.heroSectionRef?.nativeElement) {
        this.resizeObserver.observe(this.heroSectionRef.nativeElement);
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.resizeListener);
    }
  }

  ngOnDestroy(): void {
    if (this.translationSubscription) {
      this.translationSubscription.unsubscribe();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  handleButtonClick(): void {
    if (this.onButtonClick) {
      this.onButtonClick();
    }
  }

  checkScreenSize(): void {
    if (typeof window !== 'undefined') {
      this.isMobile = window.innerWidth <= 767;
    }
  }

  getBackgroundImage(): string {
    if (this.variant === 'storyAndMetrics' && this.isMobile) {
      return '/assets/images/home/new-hero-bg-mobile.png';
    }
    return this.backgroundImage;
  }

  processHeadline() {
    const len = 20 // number of chars per line headline string
    const headline = this.languageService.translate(this.headline);
    for ( let i = 0, str = '', tknIdx = 0, tokens = headline.split( ' ' ); i < 3; ++i, str = '' ) {
      while ( tokens[ tknIdx ] && ( str.length + tokens[ tknIdx ].length < len )) {
        str += tokens[ tknIdx++ ] + ' '
      }
      this.headlineProcessed[ i ] = str
    }
  }

  private updateFooterPadding(): void {
    if (!this.footerPaddingRef?.nativeElement || !this.statsBannerRef?.nativeElement || !this.heroSectionRef?.nativeElement) {
      return;
    }

    const statsBanner = this.statsBannerRef.nativeElement;
    const heroSection = this.heroSectionRef.nativeElement;
    const footerPadding = this.footerPaddingRef.nativeElement;

    // Get bounding rectangles
    const statsBannerRect = statsBanner.getBoundingClientRect();
    const heroSectionRect = heroSection.getBoundingClientRect();

    // Calculate how much the stats banner extends below the hero section
    const statsBannerBottom = statsBannerRect.bottom;
    const heroSectionBottom = heroSectionRect.bottom;
    const overflow = Math.max(0, statsBannerBottom - heroSectionBottom);

    // Set the footer padding height to match the overflow
    footerPadding.style.height = `${overflow}px`;
  }
}
